import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { Button, ButtonVariant } from '@ui/components/base/button';
import { Input } from '@ui/components/base/input';
import { Textarea } from '@ui/components/base/textarea';
import { cn } from '@ui/lib/utils';

type InputGroupProps = React.ComponentProps<'div'>;

const InputGroup = React.forwardRef<HTMLDivElement, InputGroupProps>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="input-group"
    role="group"
    className={cn(
      'group/input-group relative flex h-8 w-full min-w-0 items-center rounded-lg border border-input',
      'transition-colors outline-none',
      'in-data-[slot=combobox-content]:focus-within:border-inherit in-data-[slot=combobox-content]:focus-within:ring-0',
      'has-disabled:bg-input/50 has-disabled:opacity-50',
      'has-[[data-slot=input-group-control]:focus-visible]:border-ring',
      'has-[[data-slot=input-group-control]:focus-visible]:ring-3',
      'has-[[data-slot=input-group-control]:focus-visible]:ring-ring/50',
      'has-[[data-slot][aria-invalid=true]]:border-destructive',
      'has-[[data-slot][aria-invalid=true]]:ring-3 has-[[data-slot][aria-invalid=true]]:ring-destructive/20',
      'has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col',
      'has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col',
      'has-[>textarea]:h-auto',
      'dark:bg-input/30 dark:has-disabled:bg-input/80',
      'dark:has-[[data-slot][aria-invalid=true]]:ring-destructive/40',
      'has-[>[data-align=block-end]]:[&>input]:pt-3 has-[>[data-align=block-start]]:[&>input]:pb-3',
      'has-[>[data-align=inline-end]]:[&>input]:pr-1.5 has-[>[data-align=inline-start]]:[&>input]:pl-1.5',
      className
    )}
    {...props}
  />
));
InputGroup.displayName = 'InputGroup';

const inputGroupAddonVariants = cva(
  cn(
    'flex h-auto cursor-text items-center justify-center gap-2 py-1.5 text-sm font-medium',
    'text-muted-foreground select-none group-data-[disabled=true]/input-group:opacity-50',
    "[&>kbd]:rounded-[calc(var(--radius)-5px)] [&>svg:not([class*='size-'])]:size-4"
  ),
  {
    variants: {
      align: {
        'inline-start': 'order-first pl-2 has-[>button]:ml-[-0.3rem] has-[>kbd]:ml-[-0.15rem]',
        'inline-end': 'order-last pr-2 has-[>button]:mr-[-0.3rem] has-[>kbd]:mr-[-0.15rem]',
        'block-start':
          'order-first w-full justify-start px-2.5 pt-2 group-has-[>input]/input-group:pt-2 [.border-b]:pb-2',
        'block-end': 'order-last w-full justify-start px-2.5 pb-2 group-has-[>input]/input-group:pb-2 [.border-t]:pt-2',
      },
    },
    defaultVariants: {
      align: 'inline-start',
    },
  }
);

type InputGroupAddonProps = React.ComponentProps<'div'> & VariantProps<typeof inputGroupAddonVariants>;

const focusNestedInput = (e: React.MouseEvent<HTMLDivElement>) => {
  if ((e.target as HTMLElement).closest('button')) {
    return;
  }
  e.currentTarget.parentElement?.querySelector('input')?.focus();
};

const InputGroupAddon = ({ className, align = 'inline-start', ...props }: InputGroupAddonProps) => (
  <div
    role="group"
    data-slot="input-group-addon"
    data-align={align}
    className={cn(inputGroupAddonVariants({ align }), className)}
    onClick={focusNestedInput}
    {...props}
  />
);

const inputGroupButtonVariants = cva('flex items-center gap-2 text-sm shadow-none', {
  variants: {
    size: {
      xs: "h-6 gap-1 px-1.5 [&>svg:not([class*='size-'])]:size-3.5",
      sm: '',
      'icon-xs': 'size-6 p-0 has-[>svg]:p-0',
      'icon-sm': 'size-8 p-0 has-[>svg]:p-0',
    },
  },
  defaultVariants: {
    size: 'xs',
  },
});

interface InputGroupButtonProps
  extends Omit<React.ComponentProps<typeof Button>, 'size' | 'type'>,
    VariantProps<typeof inputGroupButtonVariants> {
  type?: 'button' | 'submit' | 'reset';
}

const InputGroupButton = React.forwardRef<HTMLButtonElement, InputGroupButtonProps>(
  ({ className, type = 'button', variant = ButtonVariant.GHOST, size = 'xs', ...props }, ref) => (
    <Button
      ref={ref}
      type={type}
      data-size={size}
      variant={variant}
      className={cn(inputGroupButtonVariants({ size }), className)}
      {...props}
    />
  )
);
InputGroupButton.displayName = 'InputGroupButton';

type InputGroupTextProps = React.ComponentProps<'span'>;

const InputGroupText = ({ className, ...props }: InputGroupTextProps) => (
  <span
    className={cn(
      'flex items-center gap-2 text-sm text-muted-foreground [&_svg]:pointer-events-none',
      "[&_svg:not([class*='size-'])]:size-4",
      className
    )}
    {...props}
  />
);

type InputGroupInputProps = React.ComponentProps<'input'>;

const InputGroupInput = React.forwardRef<HTMLInputElement, InputGroupInputProps>(({ className, ...props }, ref) => (
  <Input
    ref={ref}
    data-slot="input-group-control"
    className={cn(
      'flex-1 rounded-none border-0 bg-transparent shadow-none ring-0',
      'focus-visible:ring-0 disabled:bg-transparent aria-invalid:ring-0',
      'dark:bg-transparent dark:disabled:bg-transparent',
      className
    )}
    {...props}
  />
));
InputGroupInput.displayName = 'InputGroupInput';

type InputGroupFieldProps = React.ComponentProps<typeof InputGroup>;

const InputGroupField = React.forwardRef<HTMLDivElement, InputGroupFieldProps>(({ className, ...props }, ref) => (
  <InputGroup
    ref={ref}
    className={cn(
      'w-auto rounded-xl bg-background dark:bg-background',
      'has-[[data-slot=input-group-control]:focus-visible]:border-input',
      'has-[[data-slot=input-group-control]:focus-visible]:ring-0',
      className
    )}
    {...props}
  />
));
InputGroupField.displayName = 'InputGroupField';

type InputGroupTextareaProps = React.ComponentProps<'textarea'>;

const InputGroupTextarea = ({ className, ...props }: InputGroupTextareaProps) => (
  <Textarea
    data-slot="input-group-control"
    className={cn(
      'flex-1 resize-none rounded-none border-0 bg-transparent py-2 shadow-none ring-0',
      'focus-visible:ring-0 disabled:bg-transparent aria-invalid:ring-0',
      'dark:bg-transparent dark:disabled:bg-transparent',
      className
    )}
    {...props}
  />
);

export {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupField,
  InputGroupInput,
  InputGroupText,
  InputGroupTextarea,
};
